import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    ColumnSortPreferences,
    ColumnTemplateDirective,
    findDefaultColumnSelection,
    initColumn,
    resolveValue,
    RowActionsDirective,
    TableColumn,
    TableConfig,
    TableSearchValues
} from '@flyfreely-portal-ui/flyfreely-table';
import {
    ColumnMode,
    LongPressDirective,
    SelectionType
} from '@siemens/ngx-datatable';
import moment from 'moment';
import { Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    takeUntil
} from 'rxjs/operators';
import { FlightLogWithDuration } from '../flight-log-list-dialogue.component';

const ROW_HEIGHT = 50;
const HEADER_HEIGHT = 40;
const MIN_LIMIT = 5;
const SEARCH_DEBOUNCE = 700;
const RESIZE_DEBOUNCE = 350;

// The column-drag long-press delay is hardcoded to 500ms in the grid and not
// configurable through its API, which makes dragging feel unresponsive.
// Experiment-only runtime patch; affects every ngx-datatable in the app.
const HEADER_DRAG_PRESS_DELAY = 150;
const longPressPrototype = LongPressDirective.prototype as any;
if (!longPressPrototype.__pressDelayPatched) {
    longPressPrototype.__pressDelayPatched = true;
    const originalOnMouseDown = longPressPrototype.onMouseDown;
    longPressPrototype.onMouseDown = function (event: MouseEvent) {
        this.duration = Math.min(
            this.duration ?? HEADER_DRAG_PRESS_DELAY,
            HEADER_DRAG_PRESS_DELAY
        );
        return originalOnMouseDown.call(this, event);
    };
}

/**
 * A row pre-rendered for display: `data` holds the original row, every other
 * key holds the formatted string for that column. Mirrors static-table's
 * approach so cell bindings are property reads, not per-change-detection
 * formatter calls.
 */
interface RenderedRow {
    data: FlightLogWithDuration;
    [key: string]: any;
}

/**
 * Experimental replacement for `static-table`, backed by @siemens/ngx-datatable.
 * The API mirrors a subset of static-table's inputs/outputs, and the cell
 * templates are reused via the same `columnTemplate`/`rowActions` directives,
 * so the surrounding dialogue markup stays unchanged.
 *
 * The page size is not user configurable: it is derived from the available
 * viewport height (`pageSizeChanged`) so the table always exactly fills its
 * container without a vertical scrollbar.
 */
@Component({
    selector: 'flight-log-datatable',
    templateUrl: './flight-log-datatable.component.html',
    styleUrl: './flight-log-datatable.component.scss'
})
export class FlightLogDatatableComponent
    implements OnInit, OnChanges, AfterContentInit, AfterViewInit, OnDestroy
{
    @Input() availableColumns: TableColumn[];
    @Input() tableData: FlightLogWithDuration[];
    @Input() tableConfig: TableConfig;
    @Input() columnSorting?: ColumnSortPreferences;
    @Input() tableSearch?: TableSearchValues;
    @Input() selectedColumns?: string[];
    @Input() columnOrder?: string[];
    @Input() templateActionsWidth = 0;

    @Output() pageChanged = new EventEmitter<number>();
    @Output() sortOrderChanged = new EventEmitter<ColumnSortPreferences>();
    @Output() search = new EventEmitter<TableSearchValues>();
    @Output() selectedColumnsChanged = new EventEmitter<string[]>();
    @Output() columnOrderChanged = new EventEmitter<string[]>();
    @Output() pageSizeChanged = new EventEmitter<number>();

    @ContentChildren(ColumnTemplateDirective)
    templates: QueryList<ColumnTemplateDirective>;
    @ContentChild(RowActionsDirective) rowActionsTemplate: RowActionsDirective;

    @ViewChild('scrollRegion', { static: true })
    scrollRegion: ElementRef<HTMLDivElement>;

    readonly ColumnMode = ColumnMode;
    readonly SelectionType = SelectionType;
    readonly rowHeight = ROW_HEIGHT;
    readonly headerHeight = HEADER_HEIGHT;

    rows: RenderedRow[] = [];
    displayColumns: TableColumn[] = [];
    searchableColumns: TableColumn[] = [];
    selectableColumns: TableColumn[] = [];
    datatableSorts: { prop: string; dir: 'asc' | 'desc' }[] = [];
    selected: RenderedRow[] = [];
    filterForm = new FormGroup({});

    private allColumns: TableColumn[] = [];
    private visibleKeys: string[] = [];
    private templateLookup: { [key: string]: TemplateRef<any> } = {};
    private regionHeight$ = new Subject<number>();
    private resizeObserver?: ResizeObserver;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(private ngZone: NgZone) {}

    rowClass = (row: RenderedRow) => ({
        'row-warning': this.tableConfig?.isWarningFunction?.(row.data) ?? false
    });

    rowIdentity = (row: RenderedRow) => row?.data?.id ?? row;

    // Selection filter values can be objects (e.g. collectedBy's {id, type})
    // whose references change whenever searchOptions are rebuilt
    compareSearchOption = (a: any, b: any) =>
        JSON.stringify(a) === JSON.stringify(b);

    get selectedRows(): FlightLogWithDuration[] {
        return this.selected.map(r => r.data);
    }

    ngOnInit() {
        this.filterForm.valueChanges
            .pipe(
                debounceTime(SEARCH_DEBOUNCE),
                distinctUntilChanged(
                    (a, b) => JSON.stringify(a) === JSON.stringify(b)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(values =>
                this.search.emit(this.normaliseSearchValues(values))
            );
    }

    ngOnChanges(changes: SimpleChanges) {
        const columnsChanged =
            changes.availableColumns ||
            changes.selectedColumns ||
            changes.columnOrder;
        if (columnsChanged) {
            this.rebuildColumns();
        }
        if (changes.tableData || columnsChanged) {
            this.renderRows();
        }
        if (changes.tableData) {
            // New results mean the page, filters, or tab changed, so any
            // previous selection may reference rows that are gone.
            this.selected = [];
        }
        if (changes.columnSorting) {
            this.datatableSorts =
                this.columnSorting != null
                    ? [
                          {
                              prop: this.columnSorting.column,
                              dir: this.columnSorting.ascending
                                  ? 'asc'
                                  : 'desc'
                          }
                      ]
                    : [];
        }
        if (changes.tableSearch && this.tableSearch != null) {
            this.filterForm.patchValue(this.tableSearch, { emitEvent: false });
        }
    }

    ngAfterContentInit() {
        const buildLookup = () => {
            this.templateLookup = this.templates.reduce(
                (acc, t) => ({ ...acc, [t.column]: t.tpl }),
                {}
            );
        };
        buildLookup();
        this.templates.changes
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(buildLookup);
    }

    ngAfterViewInit() {
        this.regionHeight$
            .pipe(debounceTime(RESIZE_DEBOUNCE), takeUntil(this.ngUnsubscribe$))
            .subscribe(height => {
                const limit = Math.max(
                    MIN_LIMIT,
                    Math.floor((height - HEADER_HEIGHT) / ROW_HEIGHT)
                );
                if (limit !== this.tableConfig.limit) {
                    // ResizeObserver callbacks run outside the Angular zone
                    this.ngZone.run(() => this.pageSizeChanged.emit(limit));
                }
            });
        this.resizeObserver = new ResizeObserver(([entry]) =>
            this.regionHeight$.next(entry.contentRect.height)
        );
        this.resizeObserver.observe(this.scrollRegion.nativeElement);
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    colKey = (_: number, col: TableColumn) => col.key;

    templateFor(key: string): TemplateRef<any> | undefined {
        return this.templateLookup[key];
    }

    get actionsWidth(): number {
        return Math.max(this.templateActionsWidth, 1) * 34 + 16;
    }

    isColumnVisible(key: string): boolean {
        return this.visibleKeys.includes(key);
    }

    toggleColumn(key: string) {
        const selectedColumns = this.visibleKeys.includes(key)
            ? this.visibleKeys.filter(k => k !== key)
            : [...this.visibleKeys, key];
        this.selectedColumnsChanged.emit(selectedColumns);
    }

    onFooterPageChanged(page: number) {
        // ngx-bootstrap's pagination is 1-based and re-emits on model updates
        if (page - 1 !== (this.tableConfig.currentPage ?? 0)) {
            this.pageChanged.emit(page - 1);
        }
    }

    onSort(event: { sorts: { prop: string; dir: string }[] }) {
        const sort = event.sorts?.[0];
        if (sort == null) {
            return;
        }
        this.sortOrderChanged.emit({
            column: sort.prop,
            ascending: sort.dir === 'asc'
        });
    }

    onSelect(event: { selected: RenderedRow[] }) {
        this.selected = [...(event.selected ?? [])];
    }

    onReorder(event: { prevValue: number; newValue: number }) {
        if (event?.prevValue == null || event.newValue == null) {
            return;
        }
        // The grid reports indices over all columns: index 0 is the checkbox
        // column and the last one is the fixed actions column.
        const from = event.prevValue - 1;
        const to = Math.min(
            Math.max(event.newValue - 1, 0),
            this.displayColumns.length - 1
        );
        if (from < 0 || from >= this.displayColumns.length || from === to) {
            return;
        }
        const reordered = [...this.displayColumns];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        this.displayColumns = reordered;
        // Keep the filter toolbar in the same order as the columns
        this.searchableColumns = reordered.filter(
            c => c.searchable !== false
        );
        this.columnOrderChanged.emit(reordered.map(c => c.key));
    }

    private rebuildColumns() {
        this.allColumns = (this.availableColumns ?? []).map(initColumn);
        this.selectableColumns = this.allColumns.filter(c => c.selectable);
        this.visibleKeys =
            this.selectedColumns ?? findDefaultColumnSelection(this.allColumns);
        const orderIx = (key: string) => {
            const ix = this.columnOrder?.indexOf(key) ?? -1;
            return ix === -1 ? Number.MAX_SAFE_INTEGER : ix;
        };
        // Stable sort: ordered keys first, the rest keep definition order
        this.displayColumns = this.allColumns
            .filter(c => this.visibleKeys.includes(c.key))
            .map((c, i) => ({ c, i }))
            .sort((a, b) => orderIx(a.c.key) - orderIx(b.c.key) || a.i - b.i)
            .map(x => x.c);
        this.searchableColumns = this.displayColumns.filter(
            c => c.searchable !== false
        );
        this.syncFilterControls();
    }

    private renderRows() {
        const defaultFormatter = (val: any) =>
            val != null ? val.toString() : '';
        this.rows = (this.tableData ?? []).map(row =>
            this.allColumns.reduce(
                (acc, col) => ({
                    ...acc,
                    [col.key]:
                        (col.formatterFunction ?? defaultFormatter)(
                            resolveValue(row, col.value),
                            row
                        ) ?? ''
                }),
                { data: row } as RenderedRow
            )
        );
    }

    private syncFilterControls() {
        const wanted = this.displayColumns
            .filter(c => c.searchable !== false)
            .map(c => c.key);
        for (const key of Object.keys(this.filterForm.controls)) {
            if (!wanted.includes(key)) {
                this.filterForm.removeControl(key, { emitEvent: false });
            }
        }
        for (const key of wanted) {
            if (this.filterForm.get(key) == null) {
                this.filterForm.addControl(key, new FormControl(null), {
                    emitEvent: false
                });
            }
        }
    }

    /**
     * Widen daterange values to whole days, matching static-table's
     * behaviour, so that logs on the end date are included.
     */
    private normaliseSearchValues(values: {
        [key: string]: any;
    }): TableSearchValues {
        return Object.keys(values).reduce((acc, key) => {
            const col = this.allColumns.find(c => c.key === key);
            const value = values[key];
            if (
                col?.searchable === 'daterange' &&
                Array.isArray(value) &&
                value[0] != null &&
                value[1] != null
            ) {
                return {
                    ...acc,
                    [key]: [
                        moment(value[0]).startOf('day').toDate(),
                        moment(value[1]).endOf('day').toDate()
                    ]
                };
            }
            return { ...acc, [key]: value };
        }, {});
    }
}
